import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ShieldAlert } from 'lucide-react';

const stuOptions = [
  { id: 'head_injury', label: 'Kopf: Verletzung/Hämatom/Schwellung', normalText: 'Kopf unauffällig' },
  { id: 'neck_pain', label: 'HWS: Druckschmerz/Bewegungseinschränkung', normalText: 'HWS unauffällig' },
  { id: 'chest_injury', label: 'Thorax: Instabilität/Schmerzen', normalText: 'Thorax stabil' },
  { id: 'abdomen_pain', label: 'Abdomen: Druckschmerz/Abwehrspannung', normalText: 'Abdomen weich' },
  { id: 'pelvis_unstable', label: 'Becken: Instabilität', normalText: 'Becken stabil' },
  { id: 'spine_pain', label: 'Wirbelsäule: Druckschmerz', normalText: 'Wirbelsäule unauffällig' },
  { id: 'extremities_injury', label: 'Extremitäten: Deformität/Funktionseinschränkung', normalText: 'Extremitäten regelrecht' }
];

export const STUSection = ({ data, onChange }) => {
  const handleEnableToggle = () => {
    onChange({
      ...data,
      enabled: !data.enabled,
      findings: data.enabled ? [] : data.findings
    });
  };

  const handleToggle = (optionId) => {
    const findings = data.findings || [];
    const newFindings = findings.includes(optionId)
      ? findings.filter(id => id !== optionId)
      : [...findings, optionId];
    
    onChange({
      ...data,
      findings: newFindings
    });
  };

  return (
    <Card className="section-card border-[hsl(var(--warning))]/30">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-warning/10 rounded-lg">
            <ShieldAlert className="w-5 h-5 text-[hsl(var(--warning))]" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-2xl">Schnelle Trauma-Untersuchung (STU)</CardTitle>
            <CardDescription className="text-base mt-1">
              Systematische Ganzkörperuntersuchung bei Traumapatienten (Standardmäßig alle Bereiche unauffällig)
            </CardDescription>
          </div>
          <Switch
            id="stu-enabled"
            data-testid="stu-enabled-switch"
            aria-label="Schnelle Trauma-Untersuchung aktivieren"
            checked={data.enabled}
            onCheckedChange={handleEnableToggle}
          />
        </div>
      </CardHeader>
      {data.enabled && (
        <CardContent>
          <h4 className="text-sm font-semibold text-foreground mb-3">Abweichungen markieren (nur wenn vorhanden)</h4>
          <div className="space-y-3">
            {stuOptions.map((option) => (
              <div
                key={option.id}
                data-testid={`stu-${option.id.replaceAll('_', '-')}-option`}
                className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                  (data.findings || []).includes(option.id)
                    ? 'option-selected'
                    : 'border-border bg-card'
                }`}
                onClick={() => handleToggle(option.id)}
              >
                <Checkbox
                  id={`stu-${option.id}`}
                  data-testid={`stu-${option.id.replaceAll('_', '-')}-checkbox`}
                  onClick={event => event.stopPropagation()}
                  checked={(data.findings || []).includes(option.id)}
                  onCheckedChange={() => handleToggle(option.id)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`stu-${option.id}`}
                  onClick={event => event.stopPropagation()}
                  className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
                >
                  {option.label}
                  {option.normalText && (
                    <span className="block text-xs text-muted-foreground mt-1 italic">
                      Standard: {option.normalText}
                    </span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default STUSection;
